<template>
  <section class="payment-settings-modal">
    <header class="payment-settings-modal__header">
      <div>
        <p class="payment-settings-modal__eyebrow">Cobros</p>
        <h3>Configuración Clover</h3>
        <p class="payment-settings-modal__copy">
          Activa aquí si la reservación pública debe cobrar depósito o pago completo antes de procesarse.
        </p>
      </div>
      <button class="payment-settings-modal__close" type="button" @click="$emit('close')">×</button>
    </header>

    <div class="payment-settings-modal__card">
      <div class="payment-settings-modal__row">
        <div>
          <p class="field-label">Cobro automático</p>
          <p class="note muted">Si está activo, el cliente saldrá a Clover antes de completar la solicitud.</p>
        </div>
        <label class="toggle-switch">
          <input v-model="form.paymentEnabled" type="checkbox" />
          <span class="toggle-switch__track">
            <span class="toggle-switch__thumb"></span>
          </span>
          <span class="toggle-switch__label">{{ form.paymentEnabled ? 'Activo' : 'Inactivo' }}</span>
        </label>
      </div>

      <div class="payment-settings-modal__grid">
        <label>
          <span class="field-label">Tipo de cobro</span>
          <select v-model="form.paymentMode">
            <option value="deposit">Depósito</option>
            <option value="full">Pago completo</option>
          </select>
        </label>
        <label>
          <span class="field-label">Monto fijo por reservación</span>
          <input v-model="form.paymentAmountDisplay" type="number" min="0" step="0.01" inputmode="decimal" placeholder="25.00" />
        </label>
        <label>
          <span class="field-label">Moneda</span>
          <input v-model="form.paymentCurrency" type="text" maxlength="3" />
        </label>
        <label>
          <span class="field-label">Proveedor</span>
          <input v-model="form.paymentProvider" type="text" />
        </label>
      </div>

      <div class="payment-settings-modal__summary">
        <p class="note">
          Estado actual:
          <template v-if="form.paymentEnabled">
            {{ form.paymentMode === 'full' ? 'pago completo' : 'depósito' }} de {{ formattedPaymentAmount }}.
          </template>
          <template v-else>
            sin cobro automático.
          </template>
        </p>
        <p class="note">El monto configurado se cobra una vez por cada reservación enviada.</p>
        <p class="note">Cuando Clover confirme el pago, la solicitud entrará como pendiente para revisión del restaurante.</p>
        <p class="note muted">Ejemplo: escribe `25.00` para cobrar {{ paymentAmountExample }} por reservación.</p>
      </div>

      <div class="payment-settings-modal__overrides">
        <p class="field-label">Tarifas especiales y fechas abiertas</p>
        <p class="note muted">El rango cobra el monto especial y permite reservar esas fechas aunque estén fuera de la regla regular.</p>

        <div class="payment-settings-modal__grid payment-settings-modal__grid--override">
          <label>
            <span class="field-label">Nombre interno</span>
            <input v-model="draftOverride.label" type="text" maxlength="80" placeholder="Ej. Fin de semana de madres" />
          </label>
          <label>
            <span class="field-label">Fecha inicial</span>
            <input v-model="draftOverride.startDate" type="date" />
          </label>
          <label>
            <span class="field-label">Fecha final</span>
            <input v-model="draftOverride.endDate" type="date" />
          </label>
          <label>
            <span class="field-label">Monto fijo</span>
            <input v-model="draftOverride.paymentAmountDisplay" type="number" min="0" step="0.01" inputmode="decimal" placeholder="100.00" />
          </label>
        </div>

        <div class="payment-settings-modal__override-actions">
          <button class="btn secondary" type="button" @click="addOverride" :disabled="saving">Añadir rango especial</button>
        </div>

        <div v-if="sortedOverrides.length" class="payment-settings-modal__override-list">
          <article
            v-for="override in sortedOverrides"
            :key="override.id"
            class="payment-settings-modal__override-card"
          >
            <div>
              <p class="payment-settings-modal__override-title">{{ override.label }}</p>
              <p class="note">{{ formatOverrideDateRange(override.startDate, override.endDate) }} · {{ formatAmount(override.paymentAmount) }}</p>
            </div>
            <button class="btn ghost" type="button" @click="removeOverride(override.id)" :disabled="saving">Eliminar</button>
          </article>
        </div>
        <p v-else class="note">No hay rangos especiales configurados.</p>
      </div>

      <div v-if="webhookStatus" class="payment-settings-modal__status-card">
        <div class="payment-settings-modal__status-header">
          <div>
            <p class="field-label">Estado del webhook Clover</p>
            <p class="note muted">Aquí ves si Clover realmente está entrando y cómo se procesó el último evento.</p>
          </div>
          <button class="payment-settings-modal__refresh" type="button" :disabled="refreshingStatus" @click="$emit('refresh-status')">
            {{ refreshingStatus ? 'Actualizando...' : 'Actualizar estado' }}
          </button>
        </div>

        <div class="payment-settings-modal__status-grid">
          <div class="status-chip" :class="webhookStatus.paymentEnabled ? 'is-good' : 'is-muted'">
            {{ webhookStatus.paymentEnabled ? 'Cobro activo' : 'Cobro inactivo' }}
          </div>
          <div class="status-chip" :class="webhookStatus.merchantConfigured ? 'is-good' : 'is-bad'">
            {{ webhookStatus.merchantConfigured ? 'Merchant listo' : 'Merchant incompleto' }}
          </div>
          <div class="status-chip" :class="webhookStatus.webhookAuthConfigured ? 'is-good' : 'is-muted'">
            {{ webhookStatus.webhookAuthConfigured ? 'App webhook auth activo' : 'App webhook auth no definido' }}
          </div>
          <div class="status-chip" :class="webhookStatus.webhookSignatureConfigured ? 'is-good' : 'is-muted'">
            {{ webhookStatus.webhookSignatureConfigured ? 'Firma Hosted Checkout activa' : 'Firma Hosted Checkout no definida' }}
          </div>
          <div class="status-chip" :class="webhookStatus.meteredEventConfigured ? 'is-good' : 'is-muted'">
            {{ webhookStatus.meteredEventConfigured ? 'Metered event listo' : 'Metered event no definido' }}
          </div>
        </div>

        <div class="payment-settings-modal__status-grid payment-settings-modal__status-grid--details">
          <div>
            <span class="field-label">Merchant activo</span>
            <p class="note">{{ webhookStatus.merchant?.merchantId || 'No configurado' }}</p>
          </div>
          <div>
            <span class="field-label">Entorno</span>
            <p class="note">{{ webhookStatus.merchant?.environment || 'Sin definir' }}</p>
          </div>
          <div>
            <span class="field-label">Última verificación</span>
            <p class="note">{{ webhookStatus.status?.lastVerificationReceivedAt || 'Sin registro' }}</p>
          </div>
          <div>
            <span class="field-label">Último evento</span>
            <p class="note">{{ webhookStatus.status?.lastEventAt || 'Sin registro' }}</p>
          </div>
          <div>
            <span class="field-label">Fuente</span>
            <p class="note">{{ webhookStatus.status?.lastEventSource || 'Sin actividad' }}</p>
          </div>
          <div>
            <span class="field-label">Procesamiento</span>
            <p class="note">{{ webhookStatus.status?.lastEventProcessingStatus || 'Sin actividad' }}</p>
          </div>
          <div>
            <span class="field-label">Mensaje</span>
            <p class="note">{{ webhookStatus.status?.lastEventMessage || 'Sin mensaje' }}</p>
          </div>
          <div>
            <span class="field-label">Último error</span>
            <p class="note">{{ webhookStatus.status?.lastErrorMessage || 'Sin errores recientes' }}</p>
          </div>
        </div>
      </div>

      <div class="payment-settings-modal__actions">
        <button class="btn ghost" type="button" @click="$emit('close')" :disabled="saving">Cancelar</button>
        <button class="btn primary" type="button" @click="save" :disabled="saving">
          {{ saving ? 'Guardando...' : 'Guardar configuración' }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, reactive, watch, ref } from 'vue';
import { updateReservationAdminConfig } from '../services/api/adminConfig';
import { normalizePaymentOverrides } from '../utils/paymentOverrides';

const normalizeSettings = (settings = {}) => ({
  paymentEnabled: Boolean(settings.paymentEnabled),
  paymentMode: ['deposit', 'full'].includes(settings.paymentMode) ? settings.paymentMode : 'deposit',
  paymentAmount: Number(settings.paymentAmount) || 0,
  paymentAmountDisplay: ((Number(settings.paymentAmount) || 0) / 100).toFixed(2),
  paymentCurrency: String(settings.paymentCurrency || 'USD').trim().toUpperCase() || 'USD',
  paymentProvider: String(settings.paymentProvider || 'clover').trim() || 'clover',
  paymentOverrides: normalizePaymentOverrides(settings.paymentOverrides)
});

const toCents = (value) => {
  const normalized = Number.parseFloat(String(value || '0').replace(',', '.'));
  if (Number.isNaN(normalized) || normalized <= 0) {
    return 0;
  }
  return Math.round(normalized * 100);
};

export default {
  name: 'CloverPaymentSettingsModal',
  props: {
    settings: {
      type: Object,
      default: () => ({})
    },
    webhookStatus: {
      type: Object,
      default: null
    },
    refreshingStatus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'saved', 'refresh-status'],
  setup(props, { emit }) {
    const saving = ref(false);
    const form = reactive(normalizeSettings(props.settings));
    const createEmptyOverrideDraft = () => ({
      label: '',
      startDate: '',
      endDate: '',
      paymentAmountDisplay: ''
    });
    const draftOverride = reactive(createEmptyOverrideDraft());

    watch(() => props.settings, (nextSettings) => {
      Object.assign(form, normalizeSettings(nextSettings));
    }, { deep: true, immediate: true });

    const formattedPaymentAmount = computed(() => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: form.paymentCurrency || 'USD'
      }).format(toCents(form.paymentAmountDisplay) / 100)
    ));

    const paymentAmountExample = computed(() => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: form.paymentCurrency || 'USD'
      }).format(25)
    ));

    const formatAmount = (amountInCents) => (
      new Intl.NumberFormat('es-PR', {
        style: 'currency',
        currency: form.paymentCurrency || 'USD'
      }).format((Number(amountInCents) || 0) / 100)
    );

    const formatOverrideDateRange = (startDate, endDate) => (
      startDate === endDate
        ? startDate
        : `${startDate} a ${endDate}`
    );

    const sortedOverrides = computed(() => normalizePaymentOverrides(form.paymentOverrides));

    const addOverride = () => {
      const label = String(draftOverride.label || '').trim();
      const startDate = String(draftOverride.startDate || '').trim();
      const endDate = String(draftOverride.endDate || '').trim();
      const paymentAmount = toCents(draftOverride.paymentAmountDisplay);

      if (!label || !startDate || !endDate) {
        alert('Completa nombre y rango de fechas para el precio especial.');
        return;
      }

      if (startDate > endDate) {
        alert('La fecha inicial no puede ser mayor que la fecha final.');
        return;
      }

      if (paymentAmount <= 0) {
        alert('El monto del rango especial debe ser mayor de $0.00.');
        return;
      }

      form.paymentOverrides = normalizePaymentOverrides([
        ...form.paymentOverrides,
        {
          id: `payment-override-${Date.now()}`,
          label,
          startDate,
          endDate,
          paymentAmount,
          active: true
        }
      ]);

      Object.assign(draftOverride, createEmptyOverrideDraft());
    };

    const removeOverride = (overrideId) => {
      form.paymentOverrides = normalizePaymentOverrides(
        form.paymentOverrides.filter((entry) => entry.id !== overrideId)
      );
    };

    const save = async () => {
      saving.value = true;
      try {
        const payload = {
          paymentEnabled: Boolean(form.paymentEnabled),
          paymentMode: ['deposit', 'full'].includes(form.paymentMode) ? form.paymentMode : 'deposit',
          paymentAmount: toCents(form.paymentAmountDisplay),
          paymentCurrency: String(form.paymentCurrency || 'USD').trim().toUpperCase() || 'USD',
          paymentOverrides: normalizePaymentOverrides(form.paymentOverrides),
          autoConfirmAfterPayment: false,
          paymentProvider: String(form.paymentProvider || 'clover').trim() || 'clover'
        };
        const response = await updateReservationAdminConfig(payload);
        emit('saved', {
          ...payload,
          serviceFeeAmount: Number(props.settings.serviceFeeAmount) || 0,
          message: response.message || 'Configuración de cobro actualizada.'
        });
      } catch (error) {
        console.error('Error al guardar la configuración de cobro:', error);
        alert(error.message || 'No se pudo guardar la configuración de cobro.');
      } finally {
        saving.value = false;
      }
    };

    return {
      form,
      draftOverride,
      saving,
      formattedPaymentAmount,
      paymentAmountExample,
      sortedOverrides,
      formatAmount,
      formatOverrideDateRange,
      addOverride,
      removeOverride,
      save
    };
  }
};
</script>

<style scoped>
.payment-settings-modal {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.payment-settings-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.payment-settings-modal__eyebrow {
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: #2563eb;
  font-size: 12px;
}

.payment-settings-modal__header h3 {
  margin: 0 0 8px;
  font-size: 28px;
  color: #0f172a;
}

.payment-settings-modal__copy {
  margin: 0;
  color: #64748b;
  line-height: 1.5;
}

.payment-settings-modal__close {
  border: none;
  background: transparent;
  font-size: 30px;
  line-height: 1;
  color: #475569;
  cursor: pointer;
}

.payment-settings-modal__card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 22px;
  padding: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-settings-modal__row {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.payment-settings-modal__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.payment-settings-modal__grid label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.payment-settings-modal__grid input,
.payment-settings-modal__grid select {
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #fff;
}

.payment-settings-modal__summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.payment-settings-modal__overrides {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-settings-modal__override-actions {
  display: flex;
  justify-content: flex-end;
}

.payment-settings-modal__override-list {
  display: grid;
  gap: 8px;
}

.payment-settings-modal__override-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.9);
}

.payment-settings-modal__override-title {
  margin: 0 0 4px;
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
}

.payment-settings-modal__status-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 22px;
  padding: 18px 20px;
  background: #fffdf7;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-settings-modal__status-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.payment-settings-modal__refresh {
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  background: white;
  color: #0f172a;
  font-weight: 600;
  padding: 10px 16px;
  cursor: pointer;
}

.payment-settings-modal__refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.payment-settings-modal__status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.payment-settings-modal__status-grid--details {
  gap: 16px;
}

.status-chip {
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.status-chip.is-good {
  background: rgba(22, 163, 74, 0.12);
  color: #166534;
}

.status-chip.is-bad {
  background: rgba(220, 38, 38, 0.12);
  color: #991b1b;
}

.status-chip.is-muted {
  background: rgba(15, 23, 42, 0.08);
  color: #475569;
}

.payment-settings-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.field-label {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.note {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.note.muted {
  font-style: italic;
}

.toggle-switch {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: #0f172a;
  cursor: pointer;
}

.toggle-switch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.toggle-switch__track {
  position: relative;
  width: 58px;
  height: 32px;
  border-radius: 999px;
  background: #dbe4f0;
  transition: background 0.2s ease;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.toggle-switch__thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.22);
  transition: transform 0.2s ease;
}

.toggle-switch__label {
  font-size: 14px;
  font-weight: 600;
}

.toggle-switch input:checked + .toggle-switch__track {
  background: linear-gradient(135deg, #0f7bff, #16a34a);
}

.toggle-switch input:checked + .toggle-switch__track .toggle-switch__thumb {
  transform: translateX(26px);
}

.toggle-switch input:focus-visible + .toggle-switch__track {
  outline: 3px solid rgba(15, 123, 255, 0.22);
  outline-offset: 2px;
}

.toggle-switch--subtle {
  justify-content: flex-start;
  padding: 10px 0 2px;
}

.btn {
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  cursor: pointer;
  font-weight: 600;
}

.btn.primary {
  background: #0f7bff;
  color: #0f172a;
}

.btn.ghost {
  background: transparent;
  border: 1px solid rgba(15, 23, 42, 0.12);
  color: #0f172a;
}

.btn.secondary {
  background: #0f172a;
  color: #ffffff;
}
</style>
